import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { ConfirmDialog, Toast, useToast, useConfirm } from '../components/ConfirmDialog';
import { MdAccessTime, MdLocationOn, MdFolder, MdSearch, MdFilterList, MdEdit, MdDelete, MdAdd, MdEvent } from 'react-icons/md';
import './Events.css';

const Events = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { toasts, addToast, removeToast } = useToast();
  const { dialog, confirm } = useConfirm();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_time: '',
    end_time: '',
    location: '',
    project_id: ''
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');

  useEffect(() => {
    // Redirect non-admin users to dashboard
    if (user && user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchEvents();
    fetchProjects();
  }, [user, navigate]);

  useEffect(() => {
    filterEvents();
  }, [events, searchTerm, projectFilter]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get('/api/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/api/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const filterEvents = () => {
    let filtered = [...events];

    if (searchTerm) {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (event.location && event.location.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (projectFilter !== 'all') {
      filtered = filtered.filter(event => event.project_id === parseInt(projectFilter));
    }

    setFilteredEvents(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEvent) {
        await axios.put(`/api/events/${editingEvent.id}`, formData);
      } else {
        await axios.post('/api/events', formData);
      }
      setShowModal(false);
      setEditingEvent(null);
      setFormData({
        title: '',
        description: '',
        start_time: '',
        end_time: '',
        location: '',
        project_id: ''
      });
      fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || '',
      start_time: event.start_time ? new Date(event.start_time).toISOString().slice(0, 16) : '',
      end_time: event.end_time ? new Date(event.end_time).toISOString().slice(0, 16) : '',
      location: event.location || '',
      project_id: event.project_id || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (eventId) => {
    const ok = await confirm('Delete Event', 'Are you sure you want to delete this event?', { confirmText: 'Delete', confirmColor: '#EF4444' });
    if (!ok) return;
    try {
      await axios.delete(`/api/events/${eventId}`);
      addToast('Event deleted successfully', 'success');
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      addToast('Failed to delete event', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingEvent(null);
    setFormData({
      title: '',
      description: '',
      start_time: '',
      end_time: '',
      location: '',
      project_id: ''
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="app-container">
        <Sidebar />
        <div className="main-content events-main">
          <div className="events-page">
            <div className="page-header">
              <h1>Events</h1>
            </div>
            <div className="skeleton-list">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="skeleton-event">
                  <div className="skeleton-date"></div>
                  <div className="skeleton-content">
                    <div className="skeleton-text"></div>
                    <div className="skeleton-text short"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Sidebar />
      <Toast toasts={toasts} removeToast={removeToast} />
      <ConfirmDialog {...dialog} />
      <div className="main-content events-main">
        <div className="events-page">
          <div className="page-header">
            <div className="header-content">
              <h1><MdEvent /> Events</h1>
              <p className="header-subtitle">Schedule and track your meetings and events</p>
            </div>
            <div className="header-actions">
              <span className="count-badge">{filteredEvents.length} {filteredEvents.length === 1 ? 'Event' : 'Events'}</span>
              <button className="btn-primary" onClick={() => setShowModal(true)}>
                <MdAdd /> New Event
              </button>
            </div>
          </div>

          <div className="filters-bar">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <MdFilterList className="filter-icon" />
              <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                <option value="all">All Projects</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
          </div>

          {filteredEvents.length === 0 ? (
            <div className="empty-state">
              <p>{searchTerm || projectFilter !== 'all' ? 'No events match your filters' : 'No events scheduled. Create your first event!'}</p>
            </div>
          ) : (
            <div className="events-list">
              {filteredEvents.map(event => (
                <div key={event.id} className="event-card">
                  <div className="event-date">
                    <div className="date-day">{new Date(event.start_time).getDate()}</div>
                    <div className="date-month">
                      {new Date(event.start_time).toLocaleString('en-US', { month: 'short' })}
                    </div>
                  </div>
                  <div className="event-details">
                    <div className="event-header">
                      <h3>{event.title}</h3>
                      <div className="event-actions">
                        <button className="icon-btn edit" onClick={() => handleEdit(event)} title="Edit">
                          <MdEdit />
                        </button>
                        <button className="icon-btn delete" onClick={() => handleDelete(event.id)} title="Delete">
                          <MdDelete />
                        </button>
                      </div>
                    </div>
                    <p className="event-description">{event.description}</p>
                    <div className="event-meta">
                      <span><MdAccessTime /> {formatDate(event.start_time)}</span>
                      {event.location && <span><MdLocationOn /> {event.location}</span>}
                      {event.project_name && <span><MdFolder /> {event.project_name}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showModal && (
            <div className="modal-overlay" onClick={handleCloseModal}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h2>{editingEvent ? 'Edit Event' : 'Create New Event'}</h2>
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label>Event Title</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows="3"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Time</label>
                      <input
                        type="datetime-local"
                        value={formData.start_time}
                        onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>End Time</label>
                      <input
                        type="datetime-local"
                        value={formData.end_time}
                        onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Meeting room, online, etc."
                    />
                  </div>
                  <div className="form-group">
                    <label>Project (Optional)</label>
                    <select
                      value={formData.project_id}
                      onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
                    >
                      <option value="">No project</option>
                      {projects.map(project => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-actions">
                    <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      {editingEvent ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Events;
